import { repositoryFactory } from '@data';
import {
  calculateQuestionScore,
  calculateSectionScore,
} from '@core/algorithms';

/**
 * Progress Tracker Service
 * Manages user progress tracking and updates
 */
export class ProgressTracker {
  constructor() {
    this.courseRepository = repositoryFactory.getCourseRepository();
    this.progressRepository = repositoryFactory.getProgressRepository();
  }

  /**
   * Initialize progress for a new user
   */
  async initializeProgress(userId, courseId) {
    // Check if progress already exists
    let progress = await this.progressRepository.getUserProgress(userId, courseId);
    
    // Get course for repair/setup
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course || course.sections.length === 0) {
      console.warn('[ProgressTracker.initializeProgress] Course has no sections:', courseId);
      return progress;
    }

    if (progress) {
      // CRITICAL: Always repair sequential unlocks on load to prevent getting stuck
      // This fixes issues where sections might have been unlocked out of order
      const repairsMade = progress.repairSequentialUnlocks(course.sections);
      if (repairsMade) {
        console.warn('[ProgressTracker.initializeProgress] Corrupted progress data repaired');
        await this.progressRepository.updateUserProgress(progress);
      }
      return progress;
    }

    // Create new progress
    progress = await this.progressRepository.createUserProgress(userId, courseId);

    // Unlock first section
    if (course && course.sections.length > 0) {
      const firstSection = course.sections[0];
      progress.unlockSection(firstSection.id, course.sections);
      progress.currentSectionId = firstSection.id;
      await this.progressRepository.updateUserProgress(progress);
    }

    return progress;
  }

  /**
   * Record a user's answer and update all relevant progress
   * Captures all metrics required by PRD algorithms:
   * - timesShown, timesCorrect, timesIncorrect, lastShown, streak
   * - hintsUsed, difficulty, lastDecayAppliedAt
   */
  async recordUserAnswer(userId, courseId, questionId, answerData) {
    const { isCorrect, timeSpent, hintsUsed, userAnswer, fiftyFiftyUsed = false, reviewContentUsed = false, selectedAnswer, sessionHistoryId, skipped = false } = answerData;

    // Get question and current progress
    const question = await this.courseRepository.getQuestionById(courseId, questionId);
    if (!question) {
      throw new Error(`Question not found: ${questionId}`);
    }

    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    if (!progress) {
      throw new Error(`Progress not found for user: ${userId}`);
    }

    // Get current question progress with PRD-compliant fields
    // Fall back to the raw questionProgress map in case getQuestionProgress returns null
    // (e.g. in test mocks that haven't wired up the method to the in-memory store).
    const existingQuestionProgress =
      progress.getQuestionProgress(questionId) ??
      progress.questionProgress?.[questionId] ??
      null;
    const existingHistoryLength = Array.isArray(existingQuestionProgress?.history)
      ? existingQuestionProgress.history.length
      : 0;
    const existingTimesCorrect = existingQuestionProgress?.timesCorrect ?? 0;
    const existingTimesIncorrect = existingQuestionProgress?.timesIncorrect ?? 0;
    const existingTimesShown = Math.max(
      existingQuestionProgress?.timesShown ?? 0,
      existingQuestionProgress?.attempts ?? 0,
      existingTimesCorrect + existingTimesIncorrect,
      existingHistoryLength,
    );
    const existingCurrentScore = existingQuestionProgress?.currentScore ?? existingQuestionProgress?.score ?? 0;

    const questionProgress = existingQuestionProgress ? {
      ...existingQuestionProgress,
      timesShown: existingTimesShown,
      attempts: existingTimesShown,
      currentScore: existingCurrentScore,
      score: existingCurrentScore,
      history: Array.isArray(existingQuestionProgress.history) ? existingQuestionProgress.history : [],
      timesCorrect: existingTimesCorrect,
      timesIncorrect: existingTimesIncorrect,
      timesSkipped: existingQuestionProgress.timesSkipped ?? 0,
      hintsUsed: existingQuestionProgress.hintsUsed ?? 0,
      streak: existingQuestionProgress.streak ?? 0,
      difficulty: this._normalizeDifficulty(existingQuestionProgress.difficulty ?? question.difficulty),
    } : {
      questionId,
      sectionId: question.sectionId,
      difficulty: this._normalizeDifficulty(question.difficulty),
      
      // PRD metrics for algorithms
      timesShown: 0,                    // How many times question was presented
      timesCorrect: 0,                  // Correct attempts
      timesIncorrect: 0,                // Incorrect attempts
      timesSkipped: 0,                  // Questions skipped without answering
      lastShown: null,                  // Timestamp of last presentation
      lastCorrectAt: null,              // Timestamp of last correct answer (for tag decay)
      streak: 0,                        // Consecutive correct answers
      hintsUsed: 0,                     // Total hints used
      lastDecayAppliedAt: null,         // Last time decay was applied
      
      // Score tracking
      currentScore: 0,                  // Calculated score (0-100)
      
      // Supporting metrics
      history: [],                      // Array of { timestamp, correct, hintsUsed }
      totalTimeSpent: 0,
      averageTimeSpent: 0,
    };

    // Step 1: Update basic attempt tracking
    questionProgress.timesShown++;
    questionProgress.attempts = questionProgress.timesShown;
    questionProgress.lastShown = Date.now();
    questionProgress.totalTimeSpent += timeSpent || 0;
    
    if (questionProgress.timesShown > 0) {
      questionProgress.averageTimeSpent = questionProgress.totalTimeSpent / questionProgress.timesShown;
    }

    // Step 2: Update correctness tracking, streak, and skip count
    if (skipped) {
      questionProgress.timesSkipped = (questionProgress.timesSkipped ?? 0) + 1;
      questionProgress.streak = 0;
    } else if (isCorrect) {
      questionProgress.timesCorrect++;
      questionProgress.streak++;
      questionProgress.lastCorrectAt = Date.now(); // Track when question was last answered correctly
    } else {
      questionProgress.timesIncorrect++;
      questionProgress.streak = 0;
    }

    // Step 3: Track hints
    questionProgress.hintsUsed += hintsUsed || 0;

    // Step 4: Add to history
    questionProgress.history.push({
      timestamp: Date.now(),
      correct: isCorrect,
      hintsUsed: hintsUsed || 0,
      timeSpent: timeSpent || 0,
      fiftyFiftyUsed: fiftyFiftyUsed || false, // Track 50/50 usage
      reviewContentUsed: reviewContentUsed || false, // Track review content usage
      selectedAnswer: selectedAnswer ?? userAnswer ?? null, // The answer user selected/filled in
      skipped,
    });

    // Step 5: Calculate score using PRD algorithm
    questionProgress.currentScore = calculateQuestionScore(questionProgress, questionProgress.difficulty);
    questionProgress.score = questionProgress.currentScore;
    
    // Step 6: Apply 50/50 lifeline penalty (halve the score for this attempt)
    // Note: This reduces the overall mastery score for this question
    if (fiftyFiftyUsed && isCorrect) {
      // When 50/50 is used, reduce the weighted contribution of this correct answer
      // We do this by artificially increasing timesShown to reflect half credit
      questionProgress.currentScore = calculateQuestionScore(
        {
          ...questionProgress,
          // Effectively gives half credit by doubling the denominator
          timesShown: questionProgress.timesShown + 1
        },
        questionProgress.difficulty
      );
      questionProgress.score = questionProgress.currentScore;
    }

    // Step 7: Apply "review content used" penalty (reduces score by ~15% when correct).
    // This only applies when 50/50 was NOT also used; if both lifelines were used the
    // 50/50 penalty (step 6 — denominator inflation) already reflects a weakened answer.
    // Rationale: consulting review content before answering is weaker mastery evidence.
    if (reviewContentUsed && isCorrect && !fiftyFiftyUsed) {
      const REVIEW_CONTENT_PENALTY = 0.85; // 15% reduction (also applied in backend adaptiveRoutes.js)
      questionProgress.currentScore = Math.round(questionProgress.currentScore * REVIEW_CONTENT_PENALTY);
      questionProgress.score = questionProgress.currentScore;
    }

    // Update question progress
    await this.progressRepository.updateQuestionProgress(
      userId,
      courseId,
      questionId,
      questionProgress
    );

    // Save answer history
    await this.progressRepository.saveAnswerHistory(userId, courseId, questionId, {
      isCorrect,
      timeSpent,
      hintsUsed,
      userAnswer,
      scoreAttempt: questionProgress.currentScore, // Score assigned for this attempt
      selectedAnswer: selectedAnswer ?? userAnswer ?? null, // The answer user selected/filled in
      sessionHistoryId: sessionHistoryId || null, // Link to session for grouping
      fiftyFiftyUsed, // Track 50/50 usage in answer history
      reviewContentUsed, // Track review content usage in answer history
      skipped, // Track whether question was skipped
    });

    // Update section progress
    await this.updateSectionProgress(userId, courseId, question.sectionId);

    // Update overall progress and last studied section
    progress.currentSectionId = question.sectionId;
    await this.updateOverallProgress(userId, courseId);

    return {
      questionProgress: questionProgress,
      newScore: questionProgress.currentScore,
    };
  }

  /**
   * Mark the start of a section visit/study session
   * Increments timesStudied counter for revisit detection
   */
  async markSectionVisitStart(userId, courseId, sectionId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    if (!progress) {
      throw new Error(`Progress not found for user: ${userId}`);
    }

    const sectionProgress = progress.getSectionProgress(sectionId) || {
      sectionId,
      unlocked: false,
      score: 0,
      contentViews: {},
      timesStudied: 0,
      lastStudiedAt: null,
      currentVisitStartedAt: null,
    };

    // Check if this is a new visit (not within the last 5 minutes)
    const now = Date.now();
    const lastVisit = sectionProgress.currentVisitStartedAt || 0;
    const timeSinceLastVisit = now - lastVisit;
    const isNewVisit = timeSinceLastVisit > 5 * 60 * 1000; // 5 minutes

    if (isNewVisit) {
      // Increment timesStudied for a new session
      sectionProgress.timesStudied = (sectionProgress.timesStudied || 0) + 1;
      sectionProgress.currentVisitStartedAt = now;
      sectionProgress.lastStudiedAt = now;

      console.log('[ProgressTracker.markSectionVisitStart] New section visit:', {
        sectionId,
        timesStudied: sectionProgress.timesStudied,
        timeSinceLastVisit: Math.round(timeSinceLastVisit / 1000 / 60),
      });

      await this.progressRepository.updateSectionProgress(userId, courseId, sectionId, sectionProgress);
    }

    return sectionProgress;
  }

  /**
   * Record content view (non-question learning material)
   * Tracks how many times each content block has been displayed
   */
  async recordContentView(userId, courseId, sectionId, contentBlockIndex = 0) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    if (!progress) {
      throw new Error(`Progress not found for user: ${userId}`);
    }

    // Initialize section progress if doesn't exist
    const sectionProgress = progress.getSectionProgress(sectionId) || {
      sectionId,
      unlocked: false,
      score: 0,
      contentViews: {},
      timesStudied: 0,
      lastStudiedAt: null,
    };

    // Track content views per block
    if (!sectionProgress.contentViews) {
      sectionProgress.contentViews = {};
    }
    
    const blockKey = typeof contentBlockIndex === 'string' && contentBlockIndex.trim().length > 0
      ? contentBlockIndex.trim()
      : `block_${contentBlockIndex}`;
    
    sectionProgress.contentViews[blockKey] = {
      count: (sectionProgress.contentViews[blockKey]?.count || 0) + 1,
      lastViewedAt: Date.now(),
      firstViewedAt: sectionProgress.contentViews[blockKey]?.firstViewedAt || Date.now(),
    };

    sectionProgress.lastStudiedAt = Date.now();

    // Update progress
    await this.progressRepository.updateSectionProgress(userId, courseId, sectionId, sectionProgress);

    return sectionProgress;
  }

  /**
   * Normalize difficulty to numeric value (0.0-1.0)
   * Handles both string and numeric difficulty ratings
   */
  _normalizeDifficulty(difficulty) {
    if (typeof difficulty === 'number') {
      return Math.max(0, Math.min(1, difficulty));
    }
    
    if (typeof difficulty === 'string') {
      const difficultyMap = {
        'easy': 0.2,
        'medium': 0.5,
        'hard': 0.8,
      };
      return difficultyMap[difficulty.toLowerCase()] || 0.5;
    }
    
    return 0.5; // Default to medium
  }

  /**
   * Update section progress based on question scores
   * Uses PRD calculateSectionScore (weighted by difficulty)
   */
  async updateSectionProgress(userId, courseId, sectionId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const questions = await this.courseRepository.getQuestionsForSection(courseId, sectionId);

    if (!progress || questions.length === 0) {
      return null;
    }

    // Build question data with normalized difficulty for scoring
    const questionData = questions.map(question => {
      const questionProgress = progress.getQuestionProgress(question.id) || {
        currentScore: 0,
        difficulty: this._normalizeDifficulty(question.difficulty),
      };
      const normalizedQuestionScore = questionProgress.currentScore ?? questionProgress.score ?? 0;

      return {
        sectionId: question.sectionId || sectionId,
        difficulty: this._normalizeDifficulty(question.difficulty),
        currentScore: normalizedQuestionScore,
        score: normalizedQuestionScore,
        timesShown: questionProgress.timesShown || 0,
      };
    });

    // Calculate section score using the PRD algorithm
    const sectionScore = calculateSectionScore(sectionId, questionData);

    // Get current section progress
    const sectionProgress = progress.getSectionProgress(sectionId) || {
      score: 0,
      questionsAttempted: 0,
      questionsCompleted: 0,
      startedAt: new Date().toISOString(),
    };

    // Update section data
    const questionsAttempted = questionData.filter(q => q.timesShown > 0).length;
    const questionsCompleted = questionData.filter(q => q.currentScore >= 70).length;

    await this.progressRepository.updateSectionProgress(userId, courseId, sectionId, {
      ...sectionProgress,
      score: sectionScore,
      questionsAttempted,
      questionsCompleted,
      totalQuestions: questions.length,
      lastActivityAt: new Date().toISOString(),
    });

    // Check if we should unlock next section (all questions attempted OR score >= 30%)
    const allQuestionsAttempted = questionsAttempted >= questions.length;
    
    console.log('[updateSectionProgress] Section progress update:', {
      sectionId,
      sectionScore,
      allQuestionsAttempted,
      questionsAttempted,
      totalQuestions: questions.length,
      currentSectionId: progress.currentSectionId,
    });
    
    if (allQuestionsAttempted || sectionScore >= 30) {
      const course = await this.courseRepository.getCourseById(courseId);
      if (course) {
        const currentSectionIndex = course.sections.findIndex(s => s.id === sectionId);
        console.log('[updateSectionProgress] Checking next section unlock:', {
          currentSectionId: sectionId,
          currentSectionIndex,
          totalSections: course.sections.length,
          allQuestionsAttempted,
          sectionScore,
        });
        
        if (currentSectionIndex >= 0 && currentSectionIndex < course.sections.length - 1) {
          const nextSection = course.sections[currentSectionIndex + 1];
          
          // Always unlock and move to next section when threshold met
          // This ensures linear progression: section 1 → 2 → 3...
          if (!progress.isSectionUnlocked(nextSection.id)) {
            console.log('[updateSectionProgress] 🔓 UNLOCKING next section:', {
              currentSectionIndex,
              currentSection: sectionId,
              nextSectionId: nextSection.id,
              nextSectionIndex: nextSection.index,
              currentUnlockedSections: progress.unlockedSections,
            });
            progress.unlockSection(nextSection.id, course.sections);
            console.log('[updateSectionProgress] ✅ Section unlocked. New unlocked list:', {
              unlockedSections: progress.unlockedSections,
            });
          } else {
            console.log('[updateSectionProgress] Next section already unlocked:', {
              nextSectionId: nextSection.id,
            });
          }
          
          // ALWAYS move to next section for linear progression
          if (progress.currentSectionId !== nextSection.id) {
            console.log('[updateSectionProgress] Moving to next section:', {
              from: sectionId,
              to: nextSection.id,
            });
            progress.currentSectionId = nextSection.id;
            await this.progressRepository.updateUserProgress(progress);
          }
        } else {
          console.log('[updateSectionProgress] No next section available (end of course)', {
            currentSectionIndex,
            totalSections: course.sections.length,
          });
        }
      }
    } else {
      console.log('[updateSectionProgress] Threshold not met for unlock:', {
        sectionScore,
        allQuestionsAttempted,
        scoreThreshold: 30,
      });
    }

    // Mark section as completed if score >= 85%
    if (sectionScore >= 85 && !progress.isSectionCompleted(sectionId)) {
      console.log('[recordAnswer] 🎯 Marking section as COMPLETED (score >= 85%)', {
        sectionId,
        sectionScore,
        completedSections: progress.completedSections,
      });
      if (typeof progress.completeSection === 'function') {
        progress.completeSection(sectionId);
      }
      await this.progressRepository.updateUserProgress(progress);
    } else if (sectionScore < 85) {
      console.log('[recordAnswer] Section score below threshold', {
        sectionId,
        sectionScore,
        threshold: 85,
      });
    }

    return sectionScore;
  }

  /**
   * Update overall course progress
   */
  async updateOverallProgress(userId, courseId) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!course || !progress) {
      return null;
    }

    // Calculate weighted average of all section scores
    let totalWeight = 0;
    let weightedSum = 0;

    for (const section of course.sections) {
      const sectionProgress = progress.getSectionProgress(section.id);
      if (sectionProgress && sectionProgress.score > 0) {
        const weight = section.questions?.length || 1;
        weightedSum += sectionProgress.score * weight;
        totalWeight += weight;
      }
    }

    const overallScore = totalWeight > 0 ? weightedSum / totalWeight : 0;

    // Update progress
    progress.overallScore = overallScore;
    await this.progressRepository.updateUserProgress(progress);

    return overallScore;
  }

  /**
   * Mark section as completed
   */
  async completeSection(userId, courseId, sectionId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    
    if (!progress) {
      throw new Error(`Progress not found for user: ${userId}`);
    }

    // Mark section as completed
    progress.completeSection(sectionId);
    await this.progressRepository.updateUserProgress(progress);

    return true;
  }

  /**
   * Get detailed progress report
   */
  async getProgressReport(userId, courseId) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!course || !progress) {
      return null;
    }

    const report = {
      userId,
      courseId,
      courseTitle: course.title,
      overallScore: progress.overallScore,
      sectionsCompleted: progress.completedSections.length,
      totalSections: course.sections.length,
      sections: [],
      totalQuestionsAttempted: 0,
      totalQuestionsCorrect: 0,
      averageScore: 0,
    };

    // Build section reports
    for (const section of course.sections) {
      const sectionProgress = progress.getSectionProgress(section.id);
      const isUnlocked = progress.isSectionUnlocked(section.id);
      const isCompleted = progress.isSectionCompleted(section.id);

      report.sections.push({
        id: section.id,
        title: section.title,
        index: section.index,
        isUnlocked,
        isCompleted,
        score: sectionProgress?.score || 0,
        questionsAttempted: sectionProgress?.questionsAttempted || 0,
        questionsCompleted: sectionProgress?.questionsCompleted || 0,
        totalQuestions: section.questions?.length || 0,
      });
    }

    // Calculate totals
    const questionProgressEntries = Object.values(progress.questionProgress);
    report.totalQuestionsAttempted = questionProgressEntries.filter(q => q.attempts > 0).length;
    report.totalQuestionsCorrect = questionProgressEntries.filter(q => q.correctAttempts > 0).length;
    
    if (questionProgressEntries.length > 0) {
      report.averageScore = questionProgressEntries.reduce((sum, q) => sum + q.score, 0) / questionProgressEntries.length;
    }

    return report;
  }

  /**
   * Get the next section to study (lowest-scoring unlocked section)
   * Used when resuming learning to focus on weaker areas
   * Considers both score and time since last study
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @param {string} currentSectionId - Current section to avoid re-selecting
   */
  async getNextSectionToStudy(userId, courseId, currentSectionId = null) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!course || !progress) {
      return null;
    }

    const orderedSections = [...(course.sections || [])].sort((a, b) => (a.index || 0) - (b.index || 0));
    if (orderedSections.length === 0) {
      return null;
    }

    const repairsMade = typeof progress.repairSequentialUnlocks === 'function'
      ? progress.repairSequentialUnlocks(orderedSections)
      : false;

    let seededInitialUnlock = false;
    if ((!progress.unlockedSections || progress.unlockedSections.length === 0) && orderedSections[0]) {
      progress.unlockSection(orderedSections[0].id, orderedSections);
      progress.currentSectionId = progress.currentSectionId || orderedSections[0].id;
      seededInitialUnlock = true;
    }

    if (repairsMade || seededInitialUnlock) {
      await this.progressRepository.updateUserProgress(progress);
    }

    // Check if we're still in linear/first-pass mode (not all sections have been visited)
    const allSections = orderedSections;
    const completedSections = progress.completedSections || [];
    
    // A section is "visited" if it has any progress recorded (attempted any questions)
    const visitedSections = allSections.filter(s => {
      const sectionProg = progress.getSectionProgress(s.id);
      return sectionProg && sectionProg.questionsAttempted > 0;
    });
    
    const hasVisitedAllSections = allSections.length > 0 && 
      visitedSections.length >= allSections.length;
    
    // CRITICAL: If not all sections have been visited at least once, we're in LINEAR mode
    // In linear mode, always return the first incomplete section (sequential progression)
    if (!hasVisitedAllSections) {
      console.log('[getNextSectionToStudy] 🔵 IN LINEAR MODE (first pass)', {
        totalSections: allSections.length,
        visitedCount: visitedSections.length,
        visitedSectionIds: visitedSections.map(s => s.id),
        completedCount: completedSections.length,
        completedSectionIds: completedSections,
        unlockedSectionIds: progress.unlockedSections || [],
        allSectionIds: allSections.map(s => s.id),
      });
      
      // In LINEAR mode: Always work on the highest-index unlocked section (the "frontier")
      // This ensures strict sequential progression: Section 1 → 2 → 3 → 4 → 5
      const unlockedSections = orderedSections.filter(s => progress.isSectionUnlocked(s.id));
      
      if (unlockedSections.length === 0) {
        console.log('[getNextSectionToStudy] LINEAR mode - no unlocked sections found, using first section fallback');
        return orderedSections[0] || null;
      }
      
      // Find the frontier (highest-index unlocked section)
      const frontierSection = unlockedSections[unlockedSections.length - 1];
      const isFrontierCompleted = completedSections.includes(frontierSection.id);
      
      console.log('[getNextSectionToStudy] LINEAR mode - frontier section:', {
        sectionId: frontierSection.id,
        sectionTitle: frontierSection.title,
        sectionIndex: frontierSection.index,
        isCompleted: isFrontierCompleted,
        score: progress.getSectionProgress(frontierSection.id)?.score || 0,
        totalUnlocked: unlockedSections.length,
      });
      
      // If frontier is not completed, keep working on it
      if (!isFrontierCompleted) {
        return frontierSection;
      }
      
      // Frontier is completed, check if next section is already unlocked
      const nextSectionIndex = frontierSection.index + 1;
      const nextSection = orderedSections.find(s => s.index === nextSectionIndex);
      
      if (nextSection && progress.isSectionUnlocked(nextSection.id)) {
        console.log('[getNextSectionToStudy] LINEAR mode - moving to next section:', {
          sectionId: nextSection.id,
          sectionTitle: nextSection.title,
          sectionIndex: nextSection.index,
        });
        return nextSection;
      }
      
      // If frontier is completed but next section not yet unlocked, return frontier
      // (This allows the unlock logic to trigger when ready)
      console.log('[getNextSectionToStudy] LINEAR mode - frontier completed, next section not unlocked yet');
      return frontierSection;
    }

    // ADAPTIVE mode: Find all unlocked sections
    let unlockedSections = orderedSections.filter(section =>
      progress.isSectionUnlocked(section.id)
    );

    // Exclude current section to avoid re-selecting it immediately
    if (currentSectionId) {
      unlockedSections = unlockedSections.filter(s => s.id !== currentSectionId);
    }

    const sectionScores = unlockedSections.map(section => {
      const sectionProgress = progress.getSectionProgress(section.id);
      const score = sectionProgress?.score || 0;
      const lastStudiedAt = sectionProgress?.lastStudiedAt ? new Date(sectionProgress.lastStudiedAt).getTime() : 0;
      const timeSinceStudy = Date.now() - lastStudiedAt;
      
      return {
        section,
        score,
        lastStudiedAt,
        timeSinceStudy,
      };
    });

    console.log('[getNextSectionToStudy] In ADAPTIVE mode (all sections visited) - evaluating by mastery:', {
      totalSections: allSections.length,
      completedSections: completedSections.length,
      unlockedCount: unlockedSections.length,
      excludingSection: currentSectionId,
      sectionData: sectionScores.map(s => ({
        id: s.section.id,
        title: s.section.title,
        score: s.score,
        timeSinceStudyMinutes: Math.round(s.timeSinceStudy / 1000 / 60),
      })),
    });

    if (sectionScores.length === 0) {
      // If all other sections are excluded, return the lowest-scoring of ALL unlocked sections
      // (This includes the current section as a fallback)
      const allUnlocked = orderedSections.filter(s => progress.isSectionUnlocked(s.id));
      if (allUnlocked.length === 0) return orderedSections[0] || null;
      
      // Find the one with lowest score (to continue improving weak areas)
      let lowestSection = allUnlocked[0];
      let lowestScore = progress.getSectionProgress(lowestSection.id)?.score || 0;
      
      for (const section of allUnlocked) {
        const score = progress.getSectionProgress(section.id)?.score || 0;
        if (score < lowestScore) {
          lowestScore = score;
          lowestSection = section;
        }
      }
      
      console.log('[getNextSectionToStudy] No other sections available - returning lowest-scoring unlocked section:', {
        sectionId: lowestSection.id,
        sectionTitle: lowestSection.title,
        score: lowestScore,
      });
      
      return lowestSection;
    }

    // Sort by: lowest score first, then by time since last study (longest ago = higher priority)
    const sorted = sectionScores.sort((a, b) => {
      // Primary: lower score wins
      if (a.score !== b.score) {
        return a.score - b.score;
      }
      // Secondary: longer time since study wins (review older content)
      return b.timeSinceStudy - a.timeSinceStudy;
    });

    const selected = sorted[0];

    console.log('[getNextSectionToStudy] Selected section:', {
      sectionId: selected.section.id,
      sectionTitle: selected.section.title,
      score: selected.score,
      timeSinceStudyMinutes: Math.round(selected.timeSinceStudy / 1000 / 60),
    });

    return selected.section;
  }

  /**
   * Calculate bucket for a question based on mastery score
   * Implements HamTestOnline 4-bucket model:
   * - Unseen: timesShown === 0
   * - Weak: 0% < score < 70%
   * - Review: 70% ≤ score < 90%
   * - Learned: score ≥ 90%
   */
  calculateQuestionBucket(questionProgress) {
    if (!questionProgress || questionProgress.timesShown === 0) {
      return 'unseen';
    }

    const score = questionProgress.currentScore || 0;
    if (score < 70) return 'weak';
    if (score < 90) return 'review';
    return 'learned';
  }

  /**
   * Get bucket counts for a section
   * Returns breakdown: { unseen, weak, review, learned, total }
   */
  async getSectionBucketBreakdown(userId, courseId, sectionId) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const section = course?.sections?.find(s => s.id === sectionId);
    const questions = section?.questions || [];

    const buckets = {
      unseen: 0,
      weak: 0,
      review: 0,
      learned: 0,
      total: questions.length,
    };

    for (const question of questions) {
      const questionProgress = progress?.getQuestionProgress(question.id) || { timesShown: 0, currentScore: 0 };
      const bucket = this.calculateQuestionBucket(questionProgress);
      buckets[bucket]++;
    }

    return buckets;
  }

  /**
   * Get bucket counts for entire course
   * Returns breakdown: { unseen, weak, review, learned, total }
   */
  async getCourseBucketBreakdown(userId, courseId) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    const buckets = {
      unseen: 0,
      weak: 0,
      review: 0,
      learned: 0,
      total: 0,
    };

    for (const section of course.sections) {
      const questions = section.questions || [];
      buckets.total += questions.length;

      for (const question of questions) {
        const questionProgress = progress?.getQuestionProgress(question.id) || { timesShown: 0, currentScore: 0 };
        const bucket = this.calculateQuestionBucket(questionProgress);
        buckets[bucket]++;
      }
    }

    return buckets;
  }

  /**
   * Get questions by bucket for a section
   */
  async getQuestionsByBucket(userId, courseId, sectionId, bucket) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const section = course?.sections?.find(s => s.id === sectionId);
    const questions = section?.questions || [];

    return questions.filter(question => {
      const questionProgress = progress?.getQuestionProgress(question.id) || { timesShown: 0, currentScore: 0 };
      return this.calculateQuestionBucket(questionProgress) === bucket;
    });
  }
}

// Export singleton instance
export const progressTracker = new ProgressTracker();
